<div class="leads-page p-6 max-w-6xl mx-auto">

  <!-- Header -->
  <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Leads</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1" *ngIf="!listMode">
        Fresh leads generated from your Ideal Customer Profile.
      </p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1" *ngIf="listMode">
        Leads from your Advanced Prospecting search.
      </p>
    </div>

    <!-- ICP selector: the generation context -->
    <div class="flex items-center gap-3" *ngIf="!isLoadingIcps && icps.length && !listMode">
      <label for="leads-icp" class="text-sm font-medium text-gray-600 dark:text-gray-300">ICP</label>
      <select
        id="leads-icp"
        class="rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500"
        [ngModel]="selectedIcpId"
        (ngModelChange)="onIcpChange($event)"
      >
        <option *ngFor="let icp of icps" [ngValue]="+icp.id">{{ icp.name }}</option>
        <option [ngValue]="CREATE_ICP_OPTION">+ Create new ICP</option>
      </select>
    </div>
  </div>

  <!-- Loading ICPs -->
  <div *ngIf="isLoadingIcps && !listMode" class="py-16 text-center text-gray-400 dark:text-gray-500">
    Loading your ICPs...
  </div>

  <!-- No ICPs at all -->
  <div *ngIf="!isLoadingIcps && !icps.length && !listMode" class="py-16 text-center">
    <p class="text-gray-600 dark:text-gray-300 mb-3">You don't have an Ideal Customer Profile yet — leads are generated from an ICP's targeting.</p>
    <a routerLink="/icp/create" class="text-violet-600 font-medium hover:underline">Create an ICP</a>
  </div>

  <!-- ═══ Prospecting list mode (?list=) ═══ -->
  <ng-container *ngIf="listMode">

    <!-- Resolving the list configuration -->
    <div *ngIf="!listConfig" class="rounded-xl border border-gray-200 bg-white p-6 animate-pulse">
      <div class="flex items-center gap-3 mb-4">
        <span class="w-5 h-5 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin"></span>
        <span class="text-sm text-gray-500">Preparing your lead list…</span>
      </div>
      <div class="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div class="h-3 bg-gray-100 rounded w-1/3 mb-6"></div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div *ngFor="let s of [1, 2]" class="h-24 rounded-xl bg-gray-100"></div>
      </div>
    </div>

    <ng-container *ngIf="listConfig">
      <!-- List header -->
      <div class="rounded-xl border border-gray-200 bg-white p-4 mb-4">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <a class="inline-flex items-center gap-1 text-xs text-violet-600 hover:underline cursor-pointer mb-1"
               (click)="leaveListMode()">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Back to ICP leads
            </a>
            <h2 class="text-lg font-semibold text-gray-900 truncate" [title]="listTitle">{{ listTitle }}</h2>
            <div class="flex flex-wrap items-center gap-2 mt-1">
              <span class="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                Unsaved prospecting list · leads expire 24 h after they're generated
              </span>
              <span class="text-xs text-gray-400">
                {{ listConfig.total_companies | number }} companies
                <ng-container *ngIf="listConfig.job_titles.length"> · {{ listConfig.job_titles.join(', ') }}</ng-container>
                <ng-container *ngIf="listConfig.departments.length"> · {{ listConfig.departments.join(', ') }}</ng-container>
              </span>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2 shrink-0">
            <button type="button"
                    class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="isGenerating || isStartingListGeneration"
                    (click)="saveIcpOpen = true">
              Save as ICP
            </button>
            <button type="button"
                    class="px-4 py-2 rounded-lg border border-violet-600 text-violet-700 text-sm font-medium hover:bg-violet-50 disabled:opacity-50"
                    [disabled]="!activeMeta.total && !leads.length"
                    (click)="openCsvExport()">
              Export
            </button>
            <button type="button"
                    class="px-4 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                    [disabled]="isDeletingList"
                    (click)="deleteListConfirmOpen = true">
              Delete list
            </button>
          </div>
        </div>
      </div>

      <!-- List generation -->
      <div class="rounded-xl border border-gray-200 bg-white p-4 mb-4 space-y-3">
        <div class="flex flex-wrap items-center gap-4">
          <span class="text-sm font-semibold text-gray-800">Lead generation</span>
          <div class="flex items-center gap-1 rounded-lg border border-gray-300 p-0.5">
            <button type="button" *ngFor="let n of [5, 10]"
                    class="px-3 py-1 rounded-md text-sm transition-colors disabled:opacity-50"
                    [ngClass]="listGenerateChoice === n ? 'bg-violet-100 text-violet-700 font-medium' : 'text-gray-600 hover:bg-gray-50'"
                    [disabled]="isGenerating || isStartingListGeneration"
                    (click)="listGenerateChoice = $any(n)">
              {{ n }} <span class="text-xs text-gray-400">· {{ listPrice(n) }} cr</span>
            </button>
            <button type="button"
                    class="px-3 py-1 rounded-md text-sm transition-colors disabled:opacity-50"
                    [ngClass]="listGenerateChoice === 'more' ? 'bg-violet-100 text-violet-700 font-medium' : 'text-gray-600 hover:bg-gray-50'"
                    [disabled]="isGenerating || isStartingListGeneration"
                    (click)="listGenerateChoice = 'more'">
              More than 10 → Export to Excel
            </button>
          </div>

          <ng-container *ngIf="listGenerateChoice !== 'more'">
            <button type="button"
                    class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    [disabled]="isGenerating || isStartingListGeneration"
                    (click)="generateListLeads()">
              <span *ngIf="isGenerating || isStartingListGeneration" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
              <span *ngIf="!isGenerating && !isStartingListGeneration">Generate {{ listGenerateChoice }} leads</span>
              <span *ngIf="isStartingListGeneration">Starting…</span>
              <span *ngIf="isGenerating">Generating… {{ activeRun?.found || 0 }}<ng-container *ngIf="activeRun?.requested">/{{ activeRun?.requested }}</ng-container></span>
            </button>
            <span class="text-xs text-gray-500">{{ listPrice($any(listGenerateChoice)) }} credits for {{ listGenerateChoice }} leads</span>
          </ng-container>

          <ng-container *ngIf="listGenerateChoice === 'more'">
            <div class="flex items-center gap-2">
              <input type="number" min="11" [max]="maxExportLeads" step="1"
                     class="w-24 rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100"
                     [(ngModel)]="listMoreCount" [disabled]="listMoreAll || isExportActive">
              <label class="flex items-center gap-1 text-sm text-gray-600">
                <input type="checkbox" class="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                       [(ngModel)]="listMoreAll" [disabled]="isExportActive">
                All<span *ngIf="listCompaniesLeft !== null" class="text-xs text-gray-400"> (~{{ listCompaniesLeft | number }})</span>
              </label>
            </div>
            <app-export-columns-picker variant="menu" [value]="listExportColumns" (valueChange)="onListExportColumnsChange($event)"
                                       [disabled]="isExportActive"></app-export-columns-picker>
            <button type="button"
                    class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    [disabled]="!listMoreCountValid || isExportActive"
                    [title]="isExportActive ? 'An export of this list is already running' : ''"
                    (click)="openListExportDialog()">
              Export to Excel…
            </button>
            <span *ngIf="!listMoreCountValid" class="text-xs text-red-600">Enter 11–{{ maxExportLeads | number }}</span>
          </ng-container>

          <div *ngIf="isGenerating && activeRun?.requested" class="w-full sm:w-64 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div class="h-full bg-violet-500 transition-all duration-500" [style.width.%]="generationProgress"></div>
          </div>
        </div>

        <!-- Automation is only available for saved ICPs -->
        <div class="flex flex-wrap items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
          <span class="text-sm font-medium text-gray-700">Daily automation</span>
          <button type="button" disabled
                  class="px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium opacity-50 cursor-not-allowed">
            Start automation
          </button>
          <span class="text-xs text-gray-500">
            <a class="text-violet-600 hover:underline cursor-pointer" (click)="saveIcpOpen = true">Save this prospecting list as an ICP</a>
            to start automation.
          </span>
        </div>
      </div>

      <!-- Excel export progress -->
      <div *ngIf="activeExport" class="rounded-xl border bg-white p-4 mb-4"
           [ngClass]="isExportActive ? 'border-violet-200' : 'border-gray-200'">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div class="flex items-center gap-2">
            <span *ngIf="isExportActive" class="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin"></span>
            <span class="text-sm font-semibold text-gray-800">
              <ng-container *ngIf="activeExport.status === 'pending'">Excel export queued</ng-container>
              <ng-container *ngIf="activeExport.status === 'processing'">Exporting leads to Excel</ng-container>
              <ng-container *ngIf="activeExport.status === 'completed'">Excel export ready</ng-container>
              <ng-container *ngIf="activeExport.status === 'completed_partial'">{{ exportStopText(activeExport) }}</ng-container>
              <ng-container *ngIf="activeExport.status === 'failed'">Export failed</ng-container>
              <ng-container *ngIf="activeExport.status === 'cancelled'">Export cancelled</ng-container>
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button *ngIf="isExportActive" type="button"
                    class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="isCancellingExport" (click)="cancelExport()">
              <span *ngIf="isCancellingExport" class="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
              {{ isCancellingExport ? 'Cancelling…' : 'Cancel' }}
            </button>
            <button *ngIf="!isExportActive && activeExport.is_downloadable" type="button"
                    class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="isDownloadingExport" (click)="downloadActiveExport()">
              <span *ngIf="isDownloadingExport" class="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
              {{ isDownloadingExport ? 'Downloading…' : 'Download' }}
            </button>
            <button *ngIf="!isExportActive" type="button"
                    class="px-3 py-1.5 rounded-lg border border-violet-600 text-violet-700 text-xs font-medium hover:bg-violet-50"
                    (click)="openExportsPage()">
              Open Exports
            </button>
            <button *ngIf="!isExportActive" type="button" class="p-1 text-gray-400 hover:text-gray-600" aria-label="Dismiss"
                    (click)="dismissExportCard()">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <div class="h-2 rounded-full bg-gray-100 overflow-hidden mb-2">
          <div class="h-full transition-all duration-500"
               [ngClass]="activeExport.status === 'failed' ? 'bg-red-400' : (activeExport.status === 'completed_partial' || activeExport.status === 'cancelled' ? 'bg-amber-400' : 'bg-violet-500')"
               [style.width.%]="isExportActive ? (activeExport.progress_percentage || 0) : 100"></div>
        </div>
        <p class="text-xs text-gray-500">
          {{ activeExport.leads_found | number }} leads ·
          {{ activeExport.companies_processed | number }}/{{ (activeExport.pool_total ?? listConfig.total_companies) | number }} companies ·
          {{ activeExport.emails_found | number }} emails ·
          {{ activeExport.credits_used | number }} credits
        </p>
        <p *ngIf="isExportActive" class="text-xs text-gray-400 mt-1">You can leave this page — the file will be on the Exports page.</p>
        <p *ngIf="activeExport.status === 'failed' && activeExport.error_message" class="text-xs text-red-600 mt-1">{{ activeExport.error_message }}</p>
        <div *ngIf="!isExportActive && activeExport.can_continue"
             class="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">
          <span class="flex-1 min-w-[12rem]">Out of credits. Add credits, then continue the export from where it stopped.</span>
          <button type="button"
                  class="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700"
                  (click)="continueExportOpen = true">
            Continue export
          </button>
        </div>
      </div>
    </ng-container>
  </ng-container>

  <ng-container *ngIf="(!listMode && !isLoadingIcps && selectedIcpId) || (listMode && listConfig)">

    <!-- Generation configuration (visible on every tab) -->
    <div class="rounded-xl border border-gray-200 bg-white p-4 mb-6 space-y-4" *ngIf="!listMode">

      <!-- Card header: title + advanced filters + hide/show toggle -->
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-sm font-semibold text-gray-800">Lead generation</span>
          <!-- Compact summary while hidden -->
          <span *ngIf="configCollapsed && settings" class="text-xs text-gray-400 truncate">
            {{ generateCount }} leads · automation
            {{ settings.daily_leads_enabled ? ('on at ' + settings.daily_leads_time) : 'off' }}
          </span>
        </div>
        <div class="flex items-center gap-3 shrink-0">
          <button type="button"
                  class="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="isGenerating"
                  (click)="openAdvanced()">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
            </svg>
            Advanced filters
            <span *ngIf="advancedFilterCount"
                  class="px-1.5 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-semibold">
              {{ advancedFilterCount }}
            </span>
          </button>
          <button type="button"
                  class="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  (click)="toggleConfigCollapsed()">
            {{ configCollapsed ? 'Show' : 'Hide' }}
            <svg class="w-3.5 h-3.5 transition-transform" [ngClass]="configCollapsed ? '' : 'rotate-180'"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
        </div>
      </div>

      <ng-container *ngIf="!configCollapsed">

      <!-- Row 1: generate control + business categories + cost -->
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <label for="generate-count" class="text-sm text-gray-600">Generate</label>
          <select
            id="generate-count"
            class="rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500"
            [(ngModel)]="generateCount"
            [disabled]="isGenerating"
          >
            <option *ngFor="let n of generateCounts" [ngValue]="n">{{ n }}</option>
          </select>
          <span class="text-sm text-gray-600">more leads</span>
        </div>

        <button
          type="button"
          class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isGenerating || settings?.remaining_manual_generations === 0"
          (click)="generateLeads()"
        >
          <span *ngIf="!isGenerating">Generate more leads</span>
          <span *ngIf="isGenerating">Generating... {{ activeRun?.found || 0 }}/{{ activeRun?.requested }}</span>
        </button>

        <a [routerLink]="['/icp/edit', selectedIcpId]"
           class="text-sm text-violet-600 font-medium hover:underline">
          Modify ICP
        </a>

        <!-- Cost + quota (scales with the selected count) -->
        <div class="text-xs text-gray-500" *ngIf="settings">
          {{ currentGenerateCost }} credits for {{ generateCount }} leads
          <span *ngIf="generateCount > settings.included_leads">
            ({{ settings.credit_cost_manual }} base + {{ settings.per_extra_lead }}/extra lead)
          </span>
          · {{ settings.remaining_manual_generations }}/{{ settings.max_manual_per_day }} manual generations left today
        </div>

        <!-- Progress bar while generating -->
        <div *ngIf="isGenerating" class="w-full sm:w-64 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div class="h-full bg-violet-500 transition-all duration-500" [style.width.%]="generationProgress"></div>
        </div>
      </div>

      <!-- Applied Advanced filters as removable chips (collapsed to 2 rows) -->
      <div *ngIf="appliedBusinessCategories.length || appliedCompanyProducts.length"
           class="space-y-1.5">
        <div #filtersChips
             class="filters-chips flex flex-wrap items-center gap-2"
             [class.filters-chips-collapsed]="!filtersExpanded">
          <span class="text-xs text-gray-500">Filters:</span>
          <span *ngFor="let category of appliedBusinessCategories"
                class="inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium pl-2.5 pr-1 py-0.5">
            {{ category }}
            <button type="button"
                    class="rounded-full p-0.5 hover:bg-violet-100 text-violet-400 hover:text-violet-700"
                    [attr.aria-label]="'Remove ' + category"
                    (click)="removeAppliedCategory(category)">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </span>
          <span *ngFor="let term of appliedCompanyProducts"
                class="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-medium pl-2.5 pr-1 py-0.5">
            {{ term }}
            <button type="button"
                    class="rounded-full p-0.5 hover:bg-sky-100 text-sky-400 hover:text-sky-700"
                    [attr.aria-label]="'Remove ' + term"
                    (click)="removeAppliedProduct(term)">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </span>
        </div>
        <button *ngIf="filtersOverflowing || filtersExpanded"
                type="button"
                class="text-xs font-medium text-violet-600 hover:text-violet-800"
                (click)="toggleFiltersExpanded()">
          {{ filtersExpanded ? 'Show less' : 'Show more' }}
        </button>
      </div>

      <!-- Row 2: daily automation, inline (previously the Configure dialog) -->
      <div class="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
        <div class="flex flex-wrap items-center gap-x-6 gap-y-3">

          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-gray-700">Daily automation</span>
            <span class="px-2 py-0.5 rounded-full text-xs font-semibold"
                  [ngClass]="settings?.daily_leads_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'">
              {{ settings?.daily_leads_enabled ? 'ON' : 'OFF' }}
            </span>
            <span *ngIf="settings?.pool_completed"
                  class="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
              Completed
            </span>
          </div>

          <!-- Schedule fields are locked while automation is running — stop it to edit. -->
          <div class="flex items-center gap-2">
            <label for="automation-count" class="text-sm text-gray-600">Leads per day</label>
            <select id="automation-count"
                    class="rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    [disabled]="settings?.daily_leads_enabled"
                    [(ngModel)]="automationCount">
              <option *ngFor="let n of generateCounts" [ngValue]="n">{{ n }}</option>
            </select>
          </div>

          <div class="flex items-center gap-2">
            <label for="automation-time" class="text-sm text-gray-600">at</label>
            <input id="automation-time" type="time"
                   class="rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                   [disabled]="settings?.daily_leads_enabled"
                   [(ngModel)]="automationTime">
            <input id="automation-tz" list="leads-timezones" placeholder="Timezone"
                   class="rounded-lg border-gray-300 text-sm w-48 focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                   [disabled]="settings?.daily_leads_enabled"
                   [(ngModel)]="automationTimezone">
            <datalist id="leads-timezones">
              <option *ngFor="let tz of timezones" [value]="tz"></option>
            </datalist>
          </div>

          <!-- Pool estimate -->
          <div class="flex items-center gap-4 text-sm text-gray-600">
            <span>
              <span class="font-semibold text-gray-800">{{ isLoadingPool ? '...' : (poolEstimate?.available_companies !== null && poolEstimate ? (poolEstimate.available_companies | number) : '—') }}</span>
              companies
            </span>
            <span>
              <span class="font-semibold text-gray-800">{{ isLoadingPool ? '...' : (poolEstimate?.persons_estimate !== null && poolEstimate ? ('~' + (poolEstimate.persons_estimate | number)) : '—') }}</span>
              persons in pool
            </span>
          </div>

          <div class="flex-1"></div>

          <!-- One button drives the state: green "Save automation" turns it
               on (with the settings above), red "Stop automation" turns it off. -->
          <button type="button"
                  class="px-4 py-1.5 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                  [ngClass]="settings?.daily_leads_enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'"
                  [disabled]="isSavingAutomation || (!settings?.daily_leads_enabled && automationMissingRequired.length > 0)"
                  [title]="!settings?.daily_leads_enabled && automationMissingRequired.length ? 'Complete the ICP to start automation' : ''"
                  (click)="saveAutomation()">
            {{ isSavingAutomation
              ? 'Saving...'
              : (settings?.daily_leads_enabled ? 'Stop automation' : 'Save automation') }}
          </button>
        </div>

        <p *ngIf="automationMissingRequired.length && !settings?.daily_leads_enabled" class="text-xs text-amber-700 mt-2">
          Some ICP fields couldn't be taken from your search ({{ automationMissingLabel }}).
          <a [routerLink]="['/icp/edit', selectedIcpId]" class="font-medium text-violet-600 hover:underline">Complete ICP</a>
          to start automation.
        </p>
        <p *ngIf="settings?.pool_completed" class="text-xs text-green-700 mt-2">
          Every company matching this ICP has already been generated — there's nothing left in the pool,
          so automation was switched off. Broaden the ICP targeting or re-enable to try again.
        </p>
        <p class="text-xs text-gray-400 mt-2" *ngIf="settings">
          Each scheduled run costs {{ settings.credit_cost_scheduled }} credits (plus {{ settings.per_extra_lead }}/lead beyond {{ settings.included_leads }})
          and is skipped with a notification if your balance is too low. Runs that find no new leads are refunded.
          Editing this ICP cancels any generation in progress.
        </p>
      </div>

      </ng-container>
    </div>

    <!-- Tabs -->
    <div class="flex flex-wrap items-center gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
      <button type="button"
              class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              [ngClass]="activeTab === 'active' ? 'border-violet-600 text-violet-700 dark:text-violet-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'"
              (click)="selectTab('active')">
        Leads <span class="ml-1 text-xs text-gray-400">{{ activeMeta.total }}</span>
      </button>
      <button type="button"
              class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              [ngClass]="activeTab === 'dismissed' ? 'border-violet-600 text-violet-700 dark:text-violet-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'"
              (click)="selectTab('dismissed')">
        Dismissed <span *ngIf="dismissedLoadedFor !== null || (listMode && !isLoadingDismissed && dismissedMeta.total > 0)" class="ml-1 text-xs text-gray-400">{{ dismissedMeta.total }}</span>
      </button>
      <button type="button" *ngIf="!listMode"
              class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              [ngClass]="activeTab === 'activity' ? 'border-violet-600 text-violet-700 dark:text-violet-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'"
              (click)="selectTab('activity')">
        Activity <span *ngIf="runsLoadedFor !== null" class="ml-1 text-xs text-gray-400">{{ runsMeta.total }}</span>
      </button>

      <div class="flex-1"></div>

      <!-- Export -->
      <div class="flex items-center pb-1 mr-3">
        <button type="button"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium shadow-sm hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                [disabled]="isExporting || !csvExportScope"
                (click)="openCsvExport()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
          </svg>
          {{ isExporting ? 'Exporting...' : 'Export leads' }}
        </button>
      </div>

      <!-- Grid / list view switcher -->
      <div class="flex items-center gap-1 pb-1 mr-3 rounded-lg border border-gray-300 p-0.5" *ngIf="activeTab !== 'activity'">
        <button type="button"
                class="p-1.5 rounded-md transition-colors"
                [ngClass]="viewMode === 'grid' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:text-gray-600'"
                title="Grid view"
                aria-label="Grid view"
                (click)="setViewMode('grid')">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/>
          </svg>
        </button>
        <button type="button"
                class="p-1.5 rounded-md transition-colors"
                [ngClass]="viewMode === 'list' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:text-gray-600'"
                title="List view"
                aria-label="List view"
                (click)="setViewMode('list')">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </button>
      </div>

      <!-- Per-page selector -->
      <div class="flex items-center gap-2 pb-1">
        <label for="leads-per-page" class="text-xs text-gray-500">Per page</label>
        <select id="leads-per-page"
                class="rounded-lg border-gray-300 text-xs focus:ring-violet-500 focus:border-violet-500"
                [ngModel]="perPage"
                (ngModelChange)="onPerPageChange($event)">
          <option *ngFor="let n of perPageOptions" [ngValue]="n">{{ n }}</option>
        </select>
      </div>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoadingCurrentTab" class="py-16 text-center text-gray-400">Loading...</div>

    <!-- Empty states -->
    <div *ngIf="!isLoadingCurrentTab && activeTab !== 'activity' && !displayedLeads.length && !(activeTab === 'active' && isGenerating)" class="py-16 text-center">
      <ng-container *ngIf="activeTab === 'active' && !listMode">
        <p class="text-gray-600 mb-1">No leads for this ICP yet.</p>
        <p class="text-sm text-gray-400">Hit "Generate more leads" to get your first batch.</p>
      </ng-container>
      <ng-container *ngIf="activeTab === 'active' && listMode">
        <p class="text-gray-600 mb-1">No leads in this list yet.</p>
        <p class="text-sm text-gray-400">Generate 5 or 10 leads above, or export more straight to Excel.</p>
      </ng-container>
      <ng-container *ngIf="activeTab === 'dismissed'">
        <p class="text-gray-600 mb-1">No dismissed leads.</p>
        <p class="text-sm text-gray-400">Leads you dismiss will land here, and can be restored anytime.</p>
      </ng-container>
    </div>
    <div *ngIf="!isLoadingCurrentTab && activeTab === 'activity' && !runs.length" class="py-16 text-center">
      <p class="text-gray-600 mb-1">No generation activity yet.</p>
      <p class="text-sm text-gray-400">Manual and daily automated generations for this ICP will appear here.</p>
    </div>

    <!-- Lead cards (Leads + Dismissed tabs, grid view) -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4" *ngIf="activeTab !== 'activity' && displayedLeads.length && viewMode === 'grid'">
      <div *ngFor="let lead of displayedLeads; trackBy: trackById"
           class="rounded-xl border bg-white p-4 flex flex-col gap-3"
           [ngClass]="isNew(lead) ? 'border-violet-300 ring-1 ring-violet-200' : 'border-gray-200'">

        <!-- Top row: avatar, name, score -->
        <div class="flex items-start gap-3">
          <img *ngIf="photoUrl(lead)"
               [src]="photoUrl(lead)"
               [alt]="displayName(lead)"
               (error)="onPhotoError(lead)"
               class="w-10 h-10 rounded-full object-cover shrink-0" />
          <div *ngIf="!photoUrl(lead)"
               class="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-semibold text-sm shrink-0">
            {{ initials(lead) }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-900 truncate">{{ displayName(lead) }}</span>
              <span *ngIf="isNew(lead)"
                    class="px-1.5 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold uppercase tracking-wide shrink-0">New</span>
              <span *ngIf="isVisited(lead)"
                    class="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium shrink-0">Viewed</span>
              <a *ngIf="lead.person_linkedin_url" [href]="lead.person_linkedin_url" target="_blank" rel="noopener"
                 (click)="markVisited(lead)"
                 class="text-xs text-violet-500 hover:underline shrink-0">LinkedIn</a>
            </div>
            <div class="text-sm text-gray-600 truncate">{{ lead.person_job_title || '—' }}</div>
            <div class="flex items-center gap-1.5 text-sm text-gray-500 min-w-0">
              <img *ngIf="lead.company_logo_url"
                   [src]="lead.company_logo_url"
                   alt=""
                   loading="lazy"
                   class="w-4 h-4 rounded-sm object-contain shrink-0 bg-white"
                   (error)="onCompanyLogoError(lead)">
              <span class="truncate">
                {{ lead.company_name }}<span *ngIf="lead.company_industry"> · {{ lead.company_industry }}</span>
              </span>
            </div>
            <div class="text-xs text-gray-400 truncate" *ngIf="lead.person_location || lead.company_location">
              {{ lead.person_location || lead.company_location }}
            </div>
          </div>
          <div class="flex flex-col items-end gap-1 shrink-0">
            <span class="px-2 py-1 rounded-lg text-xs font-semibold" [ngClass]="scoreClass(lead)">
              {{ score(lead) !== null ? ('ICP ' + score(lead)) : 'Unscored' }}
            </span>
            <span *ngIf="isBelowThreshold(lead)"
                  class="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
                  title="Included below the usual quality threshold so this run wasn't empty. Consider broadening the ICP targeting.">
              Below target
            </span>
            <!-- Generation date, same format as the old lead cards -->
            <span class="text-[11px] text-gray-400" *ngIf="lead.created_at">
              {{ lead.created_at | date:'MMM d, y' }}
            </span>
            <span *ngIf="listMode && expiresIn(lead) as expiry"
                  class="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium"
                  title="Unsaved prospecting-list leads are deleted 24 h after they're generated. Save the list as an ICP to keep them.">
              {{ expiry }}
            </span>
          </div>
        </div>

        <!-- Why this lead -->
        <div *ngIf="lead.why_this_lead">
          <button type="button" class="text-xs text-violet-600 hover:underline" (click)="toggleExpanded(lead)">
            {{ isExpanded(lead) ? 'Hide' : 'Why this lead?' }}
          </button>
          <p *ngIf="isExpanded(lead)" class="text-sm text-gray-600 mt-1">{{ lead.why_this_lead }}</p>
          <ul *ngIf="isExpanded(lead) && lead.match_factors?.length" class="mt-1 flex flex-wrap gap-1">
            <li *ngFor="let factor of lead.match_factors"
                class="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{{ factor }}</li>
          </ul>
        </div>

        <!-- More leads at this company (paid reveal, dynamic per-lead price) -->
        <div *ngIf="lead.hidden_company_leads_count" class="text-sm">
          <button type="button"
                  class="text-violet-600 hover:underline disabled:opacity-50"
                  [disabled]="revealingLeads.has(lead.id)"
                  (click)="startRevealCompanyLeads(lead)">
            {{ revealingLeads.has(lead.id)
              ? 'Revealing...'
              : '+' + lead.hidden_company_leads_count + ' more lead' + (lead.hidden_company_leads_count === 1 ? '' : 's') + ' available at this company' }}
          </button>
        </div>

        <!-- Email -->
        <div class="text-sm">
          <span *ngIf="emailStatus(lead) === 'unlocked' && lead.person_email" class="text-gray-700">{{ lead.person_email }}</span>
          <span *ngIf="emailStatus(lead) === 'not_found'" class="text-gray-400">No email available</span>
          <button *ngIf="emailStatus(lead) === 'locked'"
                  type="button"
                  class="text-violet-600 hover:underline disabled:opacity-50"
                  [disabled]="unlockingLeads.has(lead.id)"
                  (click)="startUnlockEmail(lead)">
            {{ unlockingLeads.has(lead.id) ? 'Unlocking...' : 'Unlock email' }}
          </button>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 mt-auto pt-2 border-t border-gray-100">
          <ng-container *ngIf="activeTab === 'active'">
            <button *ngIf="!lead.is_enhanced"
                    type="button"
                    class="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="enhancingLeads.has(lead.id)"
                    (click)="startGenerateReport(lead)">
              {{ enhancingLeads.has(lead.id) ? 'Generating...' : 'Generate report' }}
            </button>
            <button *ngIf="lead.is_enhanced"
                    type="button"
                    class="px-3 py-1.5 rounded-lg border border-violet-600 text-violet-600 text-xs font-medium hover:bg-violet-50"
                    (click)="openReport(lead)">
              Open report
            </button>
            <button *ngIf="lead.is_enhanced"
                    type="button"
                    class="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="enhancingLeads.has(lead.id)"
                    title="Generate this report again in another workspace"
                    (click)="startGenerateReport(lead, true)">
              {{ enhancingLeads.has(lead.id) ? 'Regenerating...' : 'Regenerate' }}
            </button>
            <div class="flex-1"></div>
            <button type="button"
                    class="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="dismissingLeads.has(lead.id)"
                    (click)="dismiss(lead)">
              Dismiss
            </button>
          </ng-container>

          <ng-container *ngIf="activeTab === 'dismissed'">
            <span class="text-xs text-gray-400">Dismissed</span>
            <div class="flex-1"></div>
            <button type="button"
                    class="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="restoringLeads.has(lead.id)"
                    (click)="restoreLead(lead)">
              {{ restoringLeads.has(lead.id) ? 'Restoring...' : 'Restore' }}
            </button>
            <button type="button"
                    class="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                    [disabled]="deletingLeads.has(lead.id)"
                    title="Permanently delete this lead"
                    (click)="startDeleteLead(lead)">
              {{ deletingLeads.has(lead.id) ? 'Deleting...' : 'Delete' }}
            </button>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Lead rows (Leads + Dismissed tabs, list view) -->
    <div class="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100"
         *ngIf="activeTab !== 'activity' && displayedLeads.length && viewMode === 'list'">
      <div *ngFor="let lead of displayedLeads; trackBy: trackById"
           class="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
           [ngClass]="isNew(lead) ? 'bg-violet-50/40' : ''">

        <!-- Avatar -->
        <img *ngIf="photoUrl(lead)"
             [src]="photoUrl(lead)"
             [alt]="displayName(lead)"
             (error)="onPhotoError(lead)"
             class="w-8 h-8 rounded-full object-cover shrink-0" />
        <div *ngIf="!photoUrl(lead)"
             class="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-semibold text-xs shrink-0">
          {{ initials(lead) }}
        </div>

        <!-- Person + company -->
        <div class="min-w-0 flex-1 basis-56">
          <div class="flex items-center gap-2 min-w-0">
            <span class="font-medium text-gray-900 truncate">{{ displayName(lead) }}</span>
            <span *ngIf="isNew(lead)"
                  class="px-1.5 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold uppercase tracking-wide shrink-0">New</span>
            <span *ngIf="isBelowThreshold(lead)"
                  class="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold shrink-0"
                  title="Included below the usual quality threshold so this run wasn't empty.">Below target</span>
            <a *ngIf="lead.person_linkedin_url" [href]="lead.person_linkedin_url" target="_blank" rel="noopener"
               (click)="markVisited(lead)"
               class="text-xs text-violet-500 hover:underline shrink-0">LinkedIn</a>
          </div>
          <div class="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
            <span class="truncate">{{ lead.person_job_title || '—' }}</span>
            <span class="text-gray-300">·</span>
            <img *ngIf="lead.company_logo_url"
                 [src]="lead.company_logo_url"
                 alt=""
                 loading="lazy"
                 class="w-3.5 h-3.5 rounded-sm object-contain shrink-0 bg-white"
                 (error)="onCompanyLogoError(lead)">
            <span class="truncate">{{ lead.company_name }}</span>
          </div>
          <!-- More leads at this company (paid reveal) -->
          <button *ngIf="lead.hidden_company_leads_count"
                  type="button"
                  class="text-xs text-violet-600 hover:underline disabled:opacity-50"
                  [disabled]="revealingLeads.has(lead.id)"
                  (click)="startRevealCompanyLeads(lead)">
            {{ revealingLeads.has(lead.id)
              ? 'Revealing...'
              : '+' + lead.hidden_company_leads_count + ' more lead' + (lead.hidden_company_leads_count === 1 ? '' : 's') + ' available at this company' }}
          </button>
        </div>

        <!-- Score -->
        <span class="px-2 py-1 rounded-lg text-xs font-semibold shrink-0" [ngClass]="scoreClass(lead)">
          {{ score(lead) !== null ? ('ICP ' + score(lead)) : 'Unscored' }}
        </span>
        <span *ngIf="listMode && expiresIn(lead) as expiry"
              class="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium shrink-0">
          {{ expiry }}
        </span>

        <!-- Email -->
        <div class="text-xs shrink-0 w-44 truncate hidden md:block">
          <span *ngIf="emailStatus(lead) === 'unlocked' && lead.person_email" class="text-gray-700">{{ lead.person_email }}</span>
          <span *ngIf="emailStatus(lead) === 'not_found'" class="text-gray-400">No email available</span>
          <button *ngIf="emailStatus(lead) === 'locked'"
                  type="button"
                  class="text-violet-600 hover:underline disabled:opacity-50"
                  [disabled]="unlockingLeads.has(lead.id)"
                  (click)="startUnlockEmail(lead)">
            {{ unlockingLeads.has(lead.id) ? 'Unlocking...' : 'Unlock email' }}
          </button>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 shrink-0 ml-auto">
          <ng-container *ngIf="activeTab === 'active'">
            <button *ngIf="!lead.is_enhanced"
                    type="button"
                    class="px-2.5 py-1 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="enhancingLeads.has(lead.id)"
                    (click)="startGenerateReport(lead)">
              {{ enhancingLeads.has(lead.id) ? 'Generating...' : 'Generate report' }}
            </button>
            <button *ngIf="lead.is_enhanced"
                    type="button"
                    class="px-2.5 py-1 rounded-lg border border-violet-600 text-violet-600 text-xs font-medium hover:bg-violet-50"
                    (click)="openReport(lead)">
              Open report
            </button>
            <button *ngIf="lead.is_enhanced"
                    type="button"
                    class="px-2.5 py-1 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="enhancingLeads.has(lead.id)"
                    title="Generate this report again in another workspace"
                    (click)="startGenerateReport(lead, true)">
              {{ enhancingLeads.has(lead.id) ? 'Regenerating...' : 'Regenerate' }}
            </button>
            <button type="button"
                    class="px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="dismissingLeads.has(lead.id)"
                    (click)="dismiss(lead)">
              Dismiss
            </button>
          </ng-container>

          <ng-container *ngIf="activeTab === 'dismissed'">
            <button type="button"
                    class="px-2.5 py-1 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
                    [disabled]="restoringLeads.has(lead.id)"
                    (click)="restoreLead(lead)">
              {{ restoringLeads.has(lead.id) ? 'Restoring...' : 'Restore' }}
            </button>
            <button type="button"
                    class="px-2.5 py-1 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                    [disabled]="deletingLeads.has(lead.id)"
                    (click)="startDeleteLead(lead)">
              {{ deletingLeads.has(lead.id) ? 'Deleting...' : 'Delete' }}
            </button>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Activity table -->
    <div *ngIf="activeTab === 'activity' && runs.length" class="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
          <tr>
            <th class="px-4 py-3">Date</th>
            <th class="px-4 py-3">Type</th>
            <th class="px-4 py-3">Status</th>
            <th class="px-4 py-3 text-right">Leads</th>
            <th class="px-4 py-3 text-right">Companies</th>
            <th class="px-4 py-3 text-right">Credits</th>
            <th class="px-4 py-3">Details</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr *ngFor="let run of runs; trackBy: trackByRunId">
            <td class="px-4 py-3 text-gray-700 whitespace-nowrap">{{ run.created_at | date:'MMM d, y HH:mm' }}</td>
            <td class="px-4 py-3 text-gray-600">{{ runTypeLabel(run) }}</td>
            <td class="px-4 py-3">
              <span class="px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="runStatusClass(run)">{{ run.status }}</span>
            </td>
            <td class="px-4 py-3 text-right text-gray-700">{{ run.leads_found }}</td>
            <td class="px-4 py-3 text-right text-gray-500">{{ run.companies_processed }}</td>
            <td class="px-4 py-3 text-right text-gray-500">{{ run.credits_consumed }}</td>
            <td class="px-4 py-3 text-xs text-gray-400 max-w-[240px] truncate" [title]="run.error_message || ''">
              {{ run.error_message || '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="!isLoadingCurrentTab && currentMeta.lastPage > 1" class="flex items-center justify-center gap-3 mt-6">
      <button type="button"
              class="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              [disabled]="currentMeta.page <= 1"
              (click)="goToPage(currentMeta.page - 1)">
        Previous
      </button>
      <span class="text-sm text-gray-500">Page {{ currentMeta.page }} of {{ currentMeta.lastPage }}</span>
      <button type="button"
              class="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              [disabled]="currentMeta.page >= currentMeta.lastPage"
              (click)="goToPage(currentMeta.page + 1)">
        Next
      </button>
    </div>

    <!-- Skeletons while generating with an empty list -->
    <div *ngIf="activeTab === 'active' && isGenerating && !leads.length" class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <div *ngFor="let s of [1, 2, 3, 4]" class="rounded-xl border border-gray-200 bg-white p-4 animate-pulse">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gray-200"></div>
          <div class="flex-1 space-y-2">
            <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            <div class="h-3 bg-gray-100 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    </div>

  </ng-container>

  <!-- Unlock-email cost confirmation (same modal as the onboarding leads table) -->
  <app-unlock-email-confirm-modal
    *ngIf="unlockTarget"
    [personName]="displayName(unlockTarget)"
    (confirm)="confirmUnlockEmail()"
    (closed)="cancelUnlockEmail()"
  ></app-unlock-email-confirm-modal>

  <!-- Reveal company leads dialog: choose how many, with a credit calculator -->
  <div *ngIf="revealPickerLead" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" (click)="cancelRevealCompanyLeads()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-sm p-6 mx-4">
      <h3 class="font-semibold text-gray-900 mb-1">More leads at {{ revealPickerLead.company_name }}</h3>
      <p class="text-sm text-gray-500 mb-4">
        {{ revealPickerLead.hidden_company_leads_count }} more qualifying
        lead{{ revealPickerLead.hidden_company_leads_count === 1 ? '' : 's' }} matched your ICP at this
        company. Choose how many to reveal — the best scores come first.
      </p>

      <div class="flex items-center justify-between gap-3 mb-4">
        <label for="reveal-count" class="text-sm text-gray-600">Leads to reveal</label>
        <select id="reveal-count"
                class="rounded-lg border-gray-300 text-sm focus:ring-violet-500 focus:border-violet-500"
                [(ngModel)]="revealCount">
          <option *ngFor="let n of revealCountOptions" [ngValue]="n">{{ n }}</option>
        </select>
      </div>

      <!-- Credit calculator -->
      <div class="rounded-lg bg-gray-50 border border-gray-100 p-3 mb-4 text-sm text-gray-700 flex items-center justify-between">
        <span>
          {{ revealCount }} × {{ settings?.company_reveal_cost_per_lead ?? 2 }} credits
        </span>
        <span class="font-semibold text-gray-900">= {{ revealCost }} credits</span>
      </div>

      <div class="flex justify-end gap-2">
        <button type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                (click)="cancelRevealCompanyLeads()">
          Cancel
        </button>
        <button type="button"
                class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
                (click)="confirmRevealCompanyLeads()">
          Reveal for {{ revealCost }} credits
        </button>
      </div>
    </div>
  </div>

  <!-- Advanced filters dialog: Company Business Category + Company Product -->
  <div *ngIf="advancedOpen" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" (click)="closeAdvanced()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[85vh] flex flex-col">
      <h3 class="font-semibold text-gray-900 mb-1">Advanced filters</h3>
      <p class="text-sm text-gray-500 mb-4">
        Refine which companies are searched for this ICP. Filters apply to
        the next generation, and are saved with the automation when you enable it.
      </p>

      <div class="overflow-y-auto pr-1 flex-1 min-h-0 space-y-5">
        <!-- Company Business Category -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-medium text-gray-700">Company Business Category</label>
            <div class="flex items-center gap-3 text-xs" *ngIf="advCategoryGroups.length">
              <button type="button" class="text-violet-600 hover:text-violet-800 font-medium"
                      (click)="selectAllAdvCategories()">Select all</button>
              <button type="button" class="text-gray-500 hover:text-gray-700 font-medium"
                      (click)="clearAdvCategories()">Clear</button>
            </div>
          </div>

          <div *ngIf="isLoadingAdvCategories" class="flex items-center gap-2 text-sm text-gray-500 py-3">
            <span class="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin"></span>
            Loading categories for this ICP's industries…
          </div>

          <div *ngIf="!isLoadingAdvCategories && !advCategoryGroups.length"
               class="text-sm text-gray-400 py-2">
            No business categories are available for this ICP's industries —
            generation will search the whole industry.
          </div>

          <p *ngIf="!isLoadingAdvCategories && advCategoryGroups.length && !advCategoriesFiltered"
             class="text-xs text-amber-600 mb-1">
            Showing all available categories — you can refine your search by
            picking the ones that fit best.
          </p>

          <div *ngIf="!isLoadingAdvCategories && advCategoryGroups.length"
               class="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
            <div *ngFor="let group of advCategoryGroups" class="p-2">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-400 px-1 py-1">
                {{ group.industry }}
              </div>
              <label *ngFor="let category of group.categories"
                     class="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                <input type="checkbox"
                       class="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                       [checked]="advSelectedCategories.has(category)"
                       (change)="toggleAdvCategory(category)">
                <span>{{ category }}</span>
              </label>
            </div>
          </div>

          <p *ngIf="!isLoadingAdvCategories && advCategoryGroups.length && advAllCategoriesSelected"
             class="text-xs text-gray-400 mt-1">
            All categories selected — the whole industry will be searched (no category filter is sent).
          </p>
        </div>

        <!-- Company Product -->
        <div>
          <label class="text-sm font-medium text-gray-700 block mb-1">Company Product</label>
          <div class="flex gap-2">
            <input type="text"
                   class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-violet-500 focus:border-violet-500"
                   placeholder="e.g. crm software, billing platform"
                   [(ngModel)]="advProductInput"
                   (keydown.enter)="addAdvProduct()">
            <button type="button"
                    class="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                    [disabled]="!advProductInput.trim() || advProducts.length >= 20"
                    (click)="addAdvProduct()">
              Add
            </button>
          </div>
          <p class="text-xs text-gray-400 mt-1">
            Each term is matched as an exact phrase against the products and services
            a company offers; multiple terms are OR-ed. Press Enter or use commas to
            add several. Up to 20 terms.
          </p>
          <div *ngIf="advProducts.length" class="flex flex-wrap gap-1.5 mt-2">
            <span *ngFor="let term of advProducts"
                  class="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs px-2 py-0.5">
              {{ term }}
              <button type="button" class="text-sky-400 hover:text-sky-700" (click)="removeAdvProduct(term)">&times;</button>
            </span>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-4 mt-2 border-t border-gray-100">
        <button type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                (click)="closeAdvanced()">
          Cancel
        </button>
        <button type="button"
                class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
                (click)="applyAdvanced()">
          Apply filters
        </button>
      </div>
    </div>
  </div>

  <!-- Delete lead confirm dialog -->
  <div *ngIf="deleteConfirmLead" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" (click)="cancelDeleteLead()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
      <h3 class="font-semibold text-gray-900 mb-1">Delete lead</h3>
      <p class="text-sm text-gray-500 mb-4">
        Permanently delete {{ displayName(deleteConfirmLead) }}? This cannot be
        undone, and since the lead leaves your history the same person may show
        up again in a future generation.
      </p>
      <div class="flex justify-end gap-2">
        <button type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                (click)="cancelDeleteLead()">
          Cancel
        </button>
        <button type="button"
                class="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                (click)="confirmDeleteLead()">
          Delete lead
        </button>
      </div>
    </div>
  </div>

  <!-- Workspace picker dialog: choose where the report lands -->
  <div *ngIf="reportPickerLead" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" (click)="cancelGenerateReport()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
      <h3 class="font-semibold text-gray-900 mb-1">{{ reportPickerRegenerate ? 'Regenerate report' : 'Generate report' }}</h3>
      <p class="text-sm text-gray-500 mb-4">
        Choose the workspace for {{ displayName(reportPickerLead) }}'s report — its
        instructions and knowledge base shape the report's insights.
        <ng-container *ngIf="reportPickerRegenerate">
          A new report will be generated in the selected workspace and "Open report"
          will point at it.
        </ng-container>
      </p>

      <div *ngIf="workspaces.length; else noWorkspaces" class="space-y-2 max-h-64 overflow-y-auto mb-4">
        <label *ngFor="let workspace of workspaces"
               class="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors"
               [ngClass]="reportWorkspaceId === +workspace.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'">
          <input type="radio" name="report-workspace" class="text-violet-600 focus:ring-violet-500"
                 [value]="+workspace.id" [(ngModel)]="reportWorkspaceId">
          <span class="min-w-0">
            <span class="block text-sm font-medium text-gray-900 truncate">
              {{ workspace.name }}
              <span *ngIf="workspace.is_default" class="ml-1 text-[10px] uppercase text-violet-500 font-semibold">Default</span>
            </span>
            <span *ngIf="workspace.icp" class="block text-xs text-gray-400 truncate">ICP: {{ workspace.icp.name }}</span>
          </span>
        </label>
      </div>
      <ng-template #noWorkspaces>
        <p class="text-sm text-gray-500 mb-4">
          No workspaces found — the report will go to your default workspace.
        </p>
      </ng-template>

      <div class="flex justify-end gap-2">
        <button type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                (click)="cancelGenerateReport()">
          Cancel
        </button>
        <button type="button"
                class="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
                (click)="confirmGenerateReport()">
          {{ reportPickerRegenerate ? 'Regenerate report' : 'Generate report' }}
        </button>
      </div>
    </div>
  </div>

  <!-- CSV export (+ optional bulk email unlock) -->
  <app-leads-export-modal
    *ngIf="csvExportOpen && csvExportScope"
    [scope]="csvExportScope"
    [includeDismissed]="!listMode && activeTab === 'dismissed'"
    [fileLabel]="csvExportLabel"
    (closed)="csvExportOpen = false"
  ></app-leads-export-modal>

  <!-- Excel export of the prospecting list (more than 10 leads) -->
  <app-list-export-dialog
    *ngIf="listExportDialogOpen && listId && listConfig"
    [listId]="listId"
    [config]="listConfig"
    [companiesLeft]="listCompaniesLeft"
    [balance]="creditBalance"
    [initialMode]="listMoreAll ? 'all' : 'custom'"
    [initialCount]="listMoreCount"
    (started)="onListExportStarted($event)"
    (closed)="closeListExportDialog()"
  ></app-list-export-dialog>

  <!-- Continue an export that ran out of credits -->
  <app-continue-export-dialog
    *ngIf="continueExportOpen && activeExport?.can_continue"
    [exp]="activeExport!"
    [balance]="creditBalance"
    (started)="onExportContinued($event)"
    (closed)="continueExportOpen = false"
  ></app-continue-export-dialog>

  <!-- Save prospecting list as ICP -->
  <app-save-list-icp-dialog
    *ngIf="saveIcpOpen && listId"
    [listId]="listId"
    [config]="listConfig"
    (saved)="onListSaved($event)"
    (closed)="saveIcpOpen = false"
  ></app-save-list-icp-dialog>

  <!-- Delete prospecting list confirm -->
  <div *ngIf="deleteListConfirmOpen && listMode" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" (click)="closeDeleteListConfirm()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
      <h3 class="font-semibold text-gray-900 mb-1">Delete prospecting list</h3>
      <p class="text-sm text-gray-500 mb-4">
        Deletes this list and its {{ listLeadCount | number }} lead{{ listLeadCount === 1 ? '' : 's' }}. This cannot be undone.
      </p>
      <div class="flex justify-end gap-2">
        <button type="button"
                class="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                [disabled]="isDeletingList"
                (click)="closeDeleteListConfirm()">
          Cancel
        </button>
        <button type="button"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                [disabled]="isDeletingList"
                (click)="confirmDeleteList()">
          <span *ngIf="isDeletingList" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
          {{ isDeletingList ? 'Deleting…' : 'Delete list' }}
        </button>
      </div>
    </div>
  </div>
</div>
